import api from "./api";

// Facility management (system-user / RBAC — gated by the "create_facilities"
// and "edit_facilities" permissions on the backend; listing/detail routes are
// unauthenticated on the backend, but the admin page still gates its own
// "view_facilities" nav entry for consistency with the rest of the panel).
//
// Like idCardService, these routes return plain, unwrapped JSON — no unwrap()
// needed.

// GET /api/facilities — optional { search, facility_status_id } filters.
// Returns a plain array of facilities: {id, facility_name, description,
// open_time, close_time, weekday_open_time, weekday_close_time,
// weekend_open_time, weekend_close_time, facility_status_id,
// facility_status, slot_count, booking_fee_id, booking_fee, created_at,
// updated_at}. open_time/close_time are a derived union
// (min(weekday_open,weekend_open) .. max(weekday_close,weekend_close)) kept
// for backward compatibility — the weekday_*/weekend_* pairs are the real,
// independently-set hours (see createFacility()/updateFacility() below).
export const listFacilities = (params = {}) =>
  api.get("/api/facilities", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/facilities/{id} — full facility details, including a `slots` array
// (each facility slot with its own `time_slots` + fee breakdown).
export const getFacility = (id) => api.get(`/api/facilities/${id}`).then((r) => r.data);

// GET /api/facility-statuses — plain array of {category_id, category_code,
// description} rows (e.g. "Active" / "Inactive") for the status dropdown.
export const listFacilityStatuses = () =>
  api.get("/api/facility-statuses").then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/facility-names — the predefined list of facility names an admin
// can pick from when adding a facility (keeps the field from drifting into
// free-text duplicates/typos). Used to populate the "Facility name" select
// on the Add Facility form.
export const listFacilityNames = () =>
  api.get("/api/facility-names").then((r) => (Array.isArray(r.data) ? r.data : []));

// POST /api/facilities  { facility_name, description, weekday_open_time,
// weekday_close_time, weekend_open_time, weekend_close_time,
// facility_status_id, slot_count, without_light_fee, without_light_ledger_code,
// with_light_fee, with_light_ledger_code, light_required_from }. All four
// weekday_*/weekend_* times are required "H:i" (e.g. "06:00") and must align
// to the seeded 30-minute time slots (05:30–20:30 per DefaultTimeSlotSeeder)
// or the backend 422s; weekday_close_time must be after weekday_open_time
// (and likewise weekend_close_time after weekend_open_time) — the two pairs
// are otherwise independent, e.g. weekday 06:00–18:00 / weekend 08:00–20:30
// is valid. The legacy single open_time/close_time pair is only consulted as
// a fallback if these four are omitted; every current caller should send
// them. Creates `slot_count` facility slots, each wired to every 30-minute
// slot across both day types at the without-light fee (the with-light
// fee/ledger code is stored on the facility but not yet wired into per-slot
// time slots). Every one of the four fee/ledger fields is required — there's
// no fallback to a single flat fee (the backend's legacy `booking_fee` field
// only covers the without-light side, and even that requires its own ledger
// code separately if a fee row doesn't already exist for that exact
// amount). `light_required_from` is optional ("H:i", nullable) — when set,
// the backend validates it against the union of both day types
// (min(weekday_open_time,weekend_open_time) .. max(weekday_close_time,
// weekend_close_time)), NOT independently against each pair — see
// FacilityOperatingHours on the backend. Booking fee calculation (see
// previewBooking()/createBooking() in bookingService.js) then charges the
// with-light rate automatically to every slot at/after that time, instead of
// leaving lighting as a manual per-booking choice. Response also includes
// facility_id, slot_capacity, generated_facility_slot_count,
// generated_time_slot_count and generated_booking_option_count alongside the
// created facility.
export const createFacility = (payload) =>
  api.post("/api/facilities", payload).then((r) => r.data);

// PUT /api/facilities/{id}  { facility_name, description, weekday_open_time,
// weekday_close_time, weekend_open_time, weekend_close_time,
// facility_status_id, without_light_fee, without_light_ledger_code,
// with_light_fee, with_light_ledger_code, light_required_from }. Slot count
// can't be changed here — update only touches the facility's own fields and
// the fee/time-slot wiring for its existing slots. Same weekday_*/weekend_*
// and fee/ledger fields as create (see createFacility() above for the
// validation rules and the light_required_from union-window nuance) — the
// backend re-resolves (or creates) the matching `income` row for each fee
// side. Omitting light_required_from from the payload keeps its current
// value; sending "" or null explicitly clears it back to
// fully-manual/optional.
export const updateFacility = (id, payload) =>
  api.put(`/api/facilities/${id}`, payload).then((r) => r.data);

// GET /api/staff/facilities/{id}/schedule?date=YYYY-MM-DD — gated by
// "view_bookings" (unlike /facilities/{id}/availability, which is
// unauthenticated). Same per-slot/time-slot grid shape as the member-facing
// availability endpoint (both share FacilityController::scheduleResponse()),
// but each time slot additionally carries `status` ("available"/"booked"/
// "full"/"blocked"/"unavailable"), `block_id`, and a `bookings` array
// ({booking_id, member_id, member_code, member_name, member_email,
// booking_status, locked_until}) — so staff can see who currently holds a
// slot instead of just available/unavailable. `date` is required or the
// backend 422s.
export const getStaffFacilitySchedule = (facilityId, date) =>
  api
    .get(`/api/staff/facilities/${facilityId}/schedule`, { params: { date } })
    .then((r) => (Array.isArray(r.data) ? r.data[0] : r.data));

// GET /api/admin/time-slots — gated by "manage_time_slots" (admin + Director
// Physical Education). Returns the master 30-minute time slots every facility
// is wired from: { day_start, day_end, total_slots, slots: [{id, start_time,
// end_time}] }, sorted by start_time, times as "H:i".
export const listTimeSlots = () => api.get("/api/admin/time-slots").then((r) => r.data);

// PUT /api/admin/time-slots/window  { start_time, end_time } — same
// "manage_time_slots" gate. Both "H:i" on :00 or :30, end_time after
// start_time, or the backend 422s. Adds every missing 30-minute master slot
// inside the window (never deletes existing ones, so it can only widen the
// overall day) and re-syncs every facility's booking options against its own
// hours. Returns { start_time, end_time, added_slot_count,
// existing_slot_count, total_slots, facilities_synced,
// booking_options_added, booking_options_updated, day_start, day_end, slots }.
export const updateTimeSlotWindow = (payload) =>
  api.put("/api/admin/time-slots/window", payload).then((r) => r.data);
